/**
 * Komal OS - CRM: scan several business cards from one photo.
 *
 * Fast-follow to single-card capture (leadcapture-module-spec.md §6) - one
 * photo of cards spread out flat and non-overlapping, one vision call
 * asking for a JSON array. Each detected card is then reviewed and
 * confirmed independently, through the same review-and-confirm step as
 * single-card capture (POST /cards/confirm, keyed by card_index into this
 * one job).
 *
 * NOT validated against real overlapping/angled cards in this session -
 * see app/crm/vision_extract.py's module docstring. Reliability is the
 * open question the spec itself flags for this approach.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileStack, UploadCloud, Loader2, CheckCircle2, AlertTriangle, RotateCcw, Clock, ListChecks, Camera, X, PenLine,
} from 'lucide-react';
import { scanCardsMulti, getScanJob, confirmCard, listEvents, createEvent, mediaUrl } from '../../services/crmService';
import useUIStore from '../../store/uiStore';

const FIELD_LABELS = {
  name: 'Name', title: 'Title', company: 'Company', phone: 'Phone',
  email: 'Email', address: 'Address', website: 'Website',
};
const FIELD_ORDER = ['name', 'title', 'company', 'phone', 'email', 'address', 'website'];

const cardStateFrom = (card) => ({
  fields: {
    name: card.name || '', title: card.title || '', company: card.company || '',
    phone: card.phone || '', email: card.email || '', address: card.address || '', website: card.website || '',
  },
  confidence: card.confidence || {},
  handwrittenNote: card.handwritten_note || '',
  confirmed: !!card.confirmed,
  skipped: false,
  dupDecision: null, // {contact, account_name}
  saving: false,
});

function CardPanel({ index, state, onChange, onNoteChange, onConfirm, onSkip, onUndo }) {
  const { fields, confidence, handwrittenNote, confirmed, skipped, dupDecision, saving } = state;

  if (confirmed) {
    return (
      <div className="glass-card p-4 flex items-center gap-3">
        <CheckCircle2 size={18} className="text-green-600 shrink-0" />
        <div className="text-sm">
          <div className="font-semibold text-gray-800">{fields.name}{fields.company ? ` — ${fields.company}` : ''}</div>
          <div className="text-gray-400 text-xs">Saved</div>
        </div>
      </div>
    );
  }
  if (skipped) {
    return (
      <div className="glass-card p-4 flex items-center justify-between gap-3 opacity-60">
        <div className="text-sm text-gray-500">Card {index + 1} — skipped</div>
        <button onClick={onUndo} className="text-xs font-medium text-komal-blue">Undo</button>
      </div>
    );
  }

  return (
    <div className="glass-card p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">Card {index + 1}</span>
        <button onClick={onSkip} className="text-gray-400 hover:text-gray-600" title="Skip this card"><X size={15} /></button>
      </div>

      {dupDecision ? (
        <>
          <div className="flex gap-3 items-start bg-amber-50 border border-amber-300 rounded-lg p-3 mb-4">
            <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
            <div className="text-sm text-gray-800">
              <div className="font-semibold">Possible existing contact</div>
              <div className="mt-0.5">
                <span className="font-medium">{dupDecision.contact.name}</span>
                {dupDecision.account_name ? ` at ${dupDecision.account_name}` : ''} already matches by email or phone.
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => onConfirm({ duplicate_decision: 'merge', matched_contact_id: dupDecision.contact.id })} disabled={saving}
              className="flex-1 text-sm font-semibold text-white bg-komal-blue rounded px-3 py-2 disabled:opacity-60">Merge into existing</button>
            <button onClick={() => onConfirm({ duplicate_decision: 'keep_separate', matched_contact_id: dupDecision.contact.id })} disabled={saving}
              className="flex-1 text-sm font-medium text-gray-700 border border-gray-300 rounded px-3 py-2 hover:bg-gray-50 disabled:opacity-60">Keep separate</button>
          </div>
        </>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            {FIELD_ORDER.map((f) => {
              const isLow = confidence[f] === 'low';
              return (
                <div key={f} className={f === 'address' ? 'sm:col-span-2' : ''}>
                  <label className="flex items-center gap-1.5 text-[10.5px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    {FIELD_LABELS[f]}
                    {isLow && <AlertTriangle size={10} className="text-amber-600" />}
                  </label>
                  <input
                    value={fields[f]}
                    onChange={(e) => onChange({ ...fields, [f]: e.target.value })}
                    className={`w-full border rounded px-2.5 py-1.5 text-sm ${isLow ? 'border-amber-300 bg-amber-50' : 'border-gray-300'}`}
                  />
                </div>
              );
            })}
          </div>
          <div className="mt-3">
            <label className="flex items-center gap-1.5 text-[10.5px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
              <PenLine size={11} /> Handwritten note (if any)
            </label>
            <textarea
              value={handwrittenNote}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder="e.g. Asked for a quote by Friday"
              rows={2}
              className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-sm"
            />
          </div>
          <button
            onClick={() => onConfirm(null)}
            disabled={saving}
            className="w-full mt-3 flex items-center justify-center gap-2 text-sm font-semibold text-white bg-green-700 rounded px-3 py-2 disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
            Confirm &amp; save
          </button>
        </>
      )}
    </div>
  );
}

export default function MultiCardScanPage() {
  const { showToast } = useUIStore();

  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState('');
  const [addingEvent, setAddingEvent] = useState(false);
  const [newEventName, setNewEventName] = useState('');

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [job, setJob] = useState(null);
  const [cards, setCards] = useState([]); // array of cardStateFrom(...)

  const fileInputRef = useRef(null);
  const pollTimer = useRef(null);

  useEffect(() => {
    listEvents().then((res) => setEvents(res.data.events)).catch(() => {});
    return () => clearTimeout(pollTimer.current);
  }, []);

  const resetScan = () => {
    setFile(null);
    setPreviewUrl(null);
    setJob(null);
    setCards([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChosen = (chosen) => {
    if (!chosen) return;
    setFile(chosen);
    setPreviewUrl(URL.createObjectURL(chosen));
    setJob(null);
    setCards([]);
  };

  const applyResult = (doneJob) => {
    const detected = doneJob.result?.cards || [];
    setCards(detected.map(cardStateFrom));
  };

  const startScan = async () => {
    if (!file) return;
    setScanning(true);
    try {
      const res = await scanCardsMulti(file, eventId || undefined);
      setJob(res.data.job);
      if (res.data.job.status === 'done') applyResult(res.data.job);
      else if (res.data.job.status === 'failed') showToast(res.data.job.error || 'Card extraction failed', 'error');
      else pollJob(res.data.job.id);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not scan the photo', 'error');
    } finally {
      setScanning(false);
    }
  };

  const pollJob = (jobId) => {
    pollTimer.current = setTimeout(async () => {
      try {
        const res = await getScanJob(jobId);
        setJob(res.data.job);
        if (res.data.job.status === 'done') applyResult(res.data.job);
        else if (res.data.job.status === 'failed') showToast(res.data.job.error || 'Card extraction failed', 'error');
        else pollJob(jobId);
      } catch {
        pollJob(jobId);
      }
    }, 5000);
  };

  const updateCard = (index, patch) => setCards((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));

  const submitCardConfirm = async (index, decision) => {
    const card = cards[index];
    if (!card.fields.name.trim() || !card.fields.company.trim()) {
      showToast('Name and company are required', 'error');
      return;
    }
    updateCard(index, { saving: true });
    try {
      const payload = {
        job_id: job.id,
        card_index: index,
        ...card.fields,
        handwritten_note: card.handwrittenNote?.trim() || null,
        event_id: eventId || null,
        duplicate_decision: decision?.duplicate_decision,
        matched_contact_id: decision?.matched_contact_id,
      };
      const res = await confirmCard(payload);
      if (res.data.status === 'needs_decision') {
        updateCard(index, { saving: false, dupDecision: res.data.duplicate });
        return;
      }
      updateCard(index, { saving: false, confirmed: true, dupDecision: null });
      showToast(`Saved ${res.data.contact.name}`, 'success');
    } catch (err) {
      updateCard(index, { saving: false });
      showToast(err.response?.data?.detail || 'Could not save this card', 'error');
    }
  };

  const handleCreateEvent = async () => {
    if (!newEventName.trim()) return;
    try {
      const res = await createEvent({ name: newEventName.trim() });
      setEvents((prev) => [res.data.event, ...prev]);
      setEventId(res.data.event.id);
      setNewEventName('');
      setAddingEvent(false);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not add event', 'error');
    }
  };

  const isBusy = job && ['pending', 'processing', 'queued'].includes(job.status);
  const isDone = job && job.status === 'done';
  const confirmedCount = cards.filter((c) => c.confirmed).length;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-white">Scan multiple cards</h1>
            <p className="text-sm text-white/70 mt-0.5">One photo, several cards spread flat and non-overlapping.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/crm/scan" className="flex items-center gap-1.5 text-xs font-medium text-white/90 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-2">
              <Camera size={14} /> Single card
            </Link>
            <Link to="/crm/queue" className="flex items-center gap-1.5 text-xs font-medium text-white/90 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-3 py-2">
              <ListChecks size={14} /> Scan queue
            </Link>
          </div>
        </div>

        <div className="glass-card p-5 mb-5">
          <label className="block text-xs font-semibold text-gray-600 mb-1.5">Event (optional)</label>
          {!addingEvent ? (
            <div className="flex gap-2">
              <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm bg-white">
                <option value="">— No event —</option>
                {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
              </select>
              <button onClick={() => setAddingEvent(true)} className="text-sm font-medium text-komal-blue px-3 py-2 border border-gray-300 rounded hover:bg-gray-50">+ New</button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input autoFocus value={newEventName} onChange={(e) => setNewEventName(e.target.value)} placeholder="e.g. IFAT India 2026" className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm" />
              <button onClick={handleCreateEvent} className="text-sm font-medium text-white bg-komal-blue px-3 py-2 rounded">Add</button>
              <button onClick={() => setAddingEvent(false)} className="text-sm text-gray-500 px-2">Cancel</button>
            </div>
          )}
        </div>

        {!job && (
          <div className="glass-card p-5 mb-5">
            {previewUrl ? (
              <div className="mb-4">
                <img src={previewUrl} alt="Cards preview" className="w-full max-h-80 object-contain rounded-lg border border-gray-200" />
              </div>
            ) : (
              <label htmlFor="crm-multi-upload" className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 hover:border-komal-blue rounded-xl py-14 cursor-pointer transition-colors">
                <FileStack size={30} className="text-gray-400 mb-2" />
                <span className="text-sm text-gray-600">Take a photo or upload up to 10 cards, spread out flat</span>
                <span className="text-xs text-gray-400 mt-1">Non-overlapping, good lighting works best</span>
              </label>
            )}
            <input id="crm-multi-upload" ref={fileInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handleFileChosen(e.target.files?.[0])} />
            <div className="flex gap-2 mt-4">
              {previewUrl && (
                <>
                  <label htmlFor="crm-multi-upload" className="text-sm font-medium text-gray-600 border border-gray-300 rounded px-3 py-2 cursor-pointer hover:bg-gray-50 flex items-center gap-1.5">
                    <UploadCloud size={14} /> Choose a different photo
                  </label>
                  <button onClick={startScan} disabled={scanning} className="flex-1 flex items-center justify-center gap-2 text-sm font-semibold text-white bg-komal-blue rounded px-3 py-2 disabled:opacity-60">
                    {scanning ? <Loader2 size={15} className="animate-spin" /> : <FileStack size={15} />}
                    {scanning ? 'Scanning…' : 'Scan cards'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {isBusy && (
          <div className="glass-card p-5 mb-5 flex items-center gap-3">
            <Loader2 size={18} className="animate-spin text-komal-blue shrink-0" />
            <div className="text-sm">
              {job.status === 'queued' ? (
                <>
                  <div className="font-semibold text-gray-800">Provider is busy — queued{job.queue_position ? ` (position ${job.queue_position})` : ''}</div>
                  <div className="text-gray-500">
                    <Clock size={12} className="inline -mt-0.5 mr-1" />
                    Next attempt in about {job.eta_seconds ? `${job.eta_seconds}s` : 'a minute'}. This page keeps checking.
                  </div>
                </>
              ) : (
                <div className="font-semibold text-gray-800">Reading the photo for cards…</div>
              )}
            </div>
          </div>
        )}

        {isDone && cards.length === 0 && (
          <div className="glass-card p-6 text-center">
            <p className="text-sm text-gray-500">No cards were found in that photo. Try again with better lighting, or use single-card scan instead.</p>
            <button onClick={resetScan} className="mt-3 flex items-center gap-1.5 mx-auto text-sm font-medium text-gray-600 border border-gray-300 rounded px-3 py-2 hover:bg-gray-50">
              <RotateCcw size={14} /> Try another photo
            </button>
          </div>
        )}

        {isDone && cards.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-sm font-semibold text-white">{cards.length} card{cards.length === 1 ? '' : 's'} found — {confirmedCount} saved</span>
              {job.image_url && <img src={mediaUrl(job.image_url)} alt="" className="w-12 h-8 object-cover rounded border border-white/30" />}
            </div>
            <div className="space-y-3">
              {cards.map((c, i) => (
                <CardPanel
                  key={i}
                  index={i}
                  state={c}
                  onChange={(fields) => updateCard(i, { fields })}
                  onNoteChange={(handwrittenNote) => updateCard(i, { handwrittenNote })}
                  onConfirm={(decision) => submitCardConfirm(i, decision)}
                  onSkip={() => updateCard(i, { skipped: true })}
                  onUndo={() => updateCard(i, { skipped: false })}
                />
              ))}
            </div>
            <button onClick={resetScan} className="mt-4 flex items-center gap-1.5 text-sm font-medium text-white/80 hover:text-white">
              <RotateCcw size={14} /> Scan another photo
            </button>
          </>
        )}
      </div>
    </div>
  );
}
